import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  MessageCircle, Plus, Send, Paperclip, X, ChevronRight, 
  Clock, CheckCircle, AlertCircle, Loader2, Image as ImageIcon,
  ArrowLeft, User, Shield, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { PageHeader } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteContext';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Status badge component
const StatusBadge = ({ status, t }) => {
  const config = {
    open: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', label: t('support.open') },
    in_progress: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', label: t('support.inProgress') },
    resolved: { color: 'bg-green-500/20 text-green-400 border-green-500/30', label: t('support.resolved') },
    closed: { color: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30', label: t('support.closed') }
  }[status] || { color: 'bg-zinc-500/20 text-zinc-400', label: status };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config.color}`}>
      {config.label}
    </span>
  );
};

// Category badge
const CategoryBadge = ({ category, t }) => {
  const config = {
    general: { color: 'text-zinc-400', label: t('support.general') },
    technical: { color: 'text-purple-400', label: t('support.technical') },
    billing: { color: 'text-green-400', label: t('support.billing') },
    other: { color: 'text-orange-400', label: t('support.other') }
  }[category] || { color: 'text-zinc-400', label: category };

  return <span className={`text-xs ${config.color}`}>{config.label}</span>;
};

// Image preview component
const ImagePreview = ({ src, onRemove, small = false }) => (
  <div className={`relative ${small ? 'w-16 h-16' : 'w-24 h-24'} rounded-lg overflow-hidden border border-zinc-700`}>
    <img src={src} alt="attachment" className="w-full h-full object-cover" />
    {onRemove && (
      <button
        onClick={onRemove}
        className="absolute top-1 right-1 p-0.5 bg-red-500 rounded-full hover:bg-red-600"
      >
        <X className="w-3 h-3 text-white" />
      </button>
    )}
  </div>
);

// Chat message component
const ChatMessage = ({ message, isUser }) => {
  const isSystem = message.sender_type === 'system';
  
  if (isSystem) {
    return (
      <div className="flex justify-center my-4">
        <div className="px-4 py-2 bg-zinc-800/50 rounded-full text-xs text-zinc-500">
          {message.message}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[80%] ${isUser ? 'order-2' : 'order-1'}`}>
        <div className="flex items-center gap-2 mb-1">
          {!isUser && (
            <div className="w-6 h-6 rounded-full bg-amber-600 flex items-center justify-center">
              <Shield className="w-3 h-3 text-white" />
            </div>
          )}
          <span className={`text-xs ${isUser ? 'text-zinc-400' : 'text-amber-400'}`}>
            {message.sender_name}
          </span>
          <span className="text-xs text-zinc-600">
            {new Date(message.created_at).toLocaleString()}
          </span>
        </div>
        <div className={`rounded-2xl p-4 ${
          isUser 
            ? 'bg-amber-600 text-white rounded-br-md' 
            : 'bg-zinc-800 text-zinc-200 rounded-bl-md'
        }`}>
          <p className="whitespace-pre-wrap text-sm">{message.message}</p>
          {message.attachments?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {message.attachments.map((src, idx) => (
                <a 
                  key={idx} 
                  href={src}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block"
                >
                  <ImagePreview src={src} small />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Loading Skeleton
const TicketListSkeleton = () => (
  <div className="space-y-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <Skeleton className="h-5 w-20 mb-2" />
            <Skeleton className="h-5 w-48 mb-2" />
            <div className="flex gap-3">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <Skeleton className="h-5 w-5" />
        </div>
      </div>
    ))}
  </div>
);

export const Support = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const { settings, loading: settingsLoading } = useSiteSettings();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageFileInputRef = useRef(null);

  // New ticket form
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    category: 'general'
  });
  const [newTicketAttachments, setNewTicketAttachments] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (token) fetchTickets();
  }, [token]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(res.data);
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTicketDetail = async (ticketId) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTicketDetail(res.data.ticket);
      setMessages(res.data.messages);
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error(t('common.fillRequired'));
      return;
    }

    try {
      setCreating(true);
      await axios.post(`${API}/tickets`, {
        ...newTicket,
        attachments: newTicketAttachments
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('support.ticketCreated'));
      setShowCreateDialog(false);
      setNewTicket({ subject: '', message: '', category: 'general' });
      setNewTicketAttachments([]);
      fetchTickets();
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setCreating(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    
    try {
      setSending(true);
      await axios.post(`${API}/tickets/${selectedTicket}/messages`, {
        message: newMessage,
        attachments: attachments
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      setAttachments([]);
      fetchTicketDetail(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('support.messageFailed'));
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      await axios.post(`${API}/tickets/${selectedTicket}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('support.ticketClosed'));
      fetchTicketDetail(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleImageUpload = async (file, forNewTicket = false) => {
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('common.invalidImage'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('common.imageTooLarge'));
      return;
    }

    try {
      setUploading(true);
      const base64 = await fileToBase64(file);
      
      if (forNewTicket) {
        setNewTicketAttachments(prev => [...prev, base64]);
      } else {
        setAttachments(prev => [...prev, base64]);
      }
      toast.success(t('common.imageAdded'));
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          // Only process if we're in the ticket detail view
          if (selectedTicket && ticketDetail) {
            await handleImageUpload(file, false);
          }
        }
      }
    }
  }, [selectedTicket, ticketDetail]);

  useEffect(() => {
    if (selectedTicket) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [selectedTicket, handlePaste]);

  const openTicket = (ticketId) => {
    setSelectedTicket(ticketId);
    fetchTicketDetail(ticketId);
  };

  const goBack = () => {
    if (selectedTicket) {
      setSelectedTicket(null);
      setTicketDetail(null);
      setMessages([]);
      setAttachments([]);
    } else {
      navigate('/profile');
    }
  };

  // Get app name
  const appName = settingsLoading ? '' : (settings?.app_name || 'App');

  // Ticket detail view
  if (selectedTicket && ticketDetail) {
    return (
      <div className="min-h-screen bg-[#FFFDF7]">
        <PageHeader title={t('support.ticketDetail')} onBack={goBack} />
        
        <div className="px-4 py-6 max-w-3xl mx-auto">
          {/* Ticket Info */}
          <div className="mb-4 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <h2 className="text-white font-semibold text-lg">{ticketDetail.subject}</h2>
            <div className="flex items-center gap-3 mt-2">
              <StatusBadge status={ticketDetail.status} t={t} />
              <CategoryBadge category={ticketDetail.category} t={t} />
              <span className="text-xs text-zinc-500">
                {new Date(ticketDetail.created_at).toLocaleDateString()}
              </span>
            </div>
            {ticketDetail.status !== 'closed' && (
              <Button
                onClick={handleCloseTicket}
                variant="ghost"
                size="sm"
                className="mt-3 text-zinc-400 hover:text-red-400"
              >
                <XCircle className="w-4 h-4 mr-1" />
                {t('support.closeTicket')}
              </Button>
            )}
          </div>

          {/* Messages */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 h-[400px] overflow-y-auto mb-4">
            {messages.map((msg) => (
              <ChatMessage key={msg.id} message={msg} isUser={msg.sender_type === 'user'} />
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Reply input */}
          {ticketDetail.status !== 'closed' ? (
            <div className="space-y-3 p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl">
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((src, idx) => (
                    <ImagePreview 
                      key={idx} 
                      src={src} 
                      onRemove={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      small
                    />
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="file"
                  ref={messageFileInputRef}
                  onChange={(e) => handleImageUpload(e.target.files?.[0], false)}
                  accept="image/*"
                  className="hidden"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => messageFileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-zinc-400 hover:text-white flex-shrink-0"
                >
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
                </Button>
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t('support.typeMessage')}
                  className="flex-1 bg-zinc-800 border-zinc-700"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                  className="bg-amber-600 hover:bg-amber-700 flex-shrink-0"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </Button>
              </div>
              <p className="text-xs text-zinc-500 text-center">{t('support.pasteImageHint')}</p>
            </div>
          ) : (
            <div className="p-4 bg-zinc-800/50 rounded-xl text-center text-zinc-500">
              {t('support.ticketIsClosed')}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Ticket list view
  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <PageHeader title={t('support.supportTickets')} onBack={() => navigate('/profile')} />
      
      <div className="px-4 py-6 max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-white font-semibold text-lg">{t('support.myTickets')}</h2>
            <p className="text-zinc-500 text-sm">
              {settingsLoading ? (
                <Skeleton className="h-4 w-40 inline-block" />
              ) : (
                t('support.helpFrom', { appName })
              )}
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-amber-600 hover:bg-amber-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            {t('support.newTicket')}
          </Button>
        </div>

        {/* Ticket list */}
        {loading ? (
          <TicketListSkeleton />
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 bg-zinc-900/50 border border-zinc-800 rounded-xl">
            <MessageCircle className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 font-medium">{t('support.noTickets')}</p>
            <p className="text-zinc-600 text-sm mt-1">{t('support.noTicketsDesc')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <motion.button
                key={ticket.id}
                onClick={() => openTicket(ticket.id)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full p-4 bg-zinc-900/50 border border-zinc-800 rounded-xl hover:bg-zinc-800/50 transition text-left"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <StatusBadge status={ticket.status} t={t} />
                      {ticket.unread_by_user && (
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                      )}
                    </div>
                    <h4 className="text-white font-medium truncate">{ticket.subject}</h4>
                    <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                      <CategoryBadge category={ticket.category} t={t} />
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                      <span>{ticket.message_count} {t('support.messages')}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                </div>
              </motion.button>
            ))}
          </div>
        )}

        {/* Create ticket dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-amber-500" />
                {t('support.newSupportTicket')}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">{t('support.category')}</label>
                <Select 
                  value={newTicket.category} 
                  onValueChange={(v) => setNewTicket(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="general">{t('support.general')}</SelectItem>
                    <SelectItem value="technical">{t('support.technical')}</SelectItem>
                    <SelectItem value="billing">{t('support.billing')}</SelectItem>
                    <SelectItem value="other">{t('support.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">{t('support.subject')} *</label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder={t('support.briefDescription')}
                  className="bg-zinc-800 border-zinc-700"
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">{t('support.message')} *</label>
                <Textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={t('support.describeIssue')}
                  className="bg-zinc-800 border-zinc-700 min-h-[120px]"
                />
              </div>

              {/* Attachments */}
              <div>
                <label className="block text-sm text-zinc-400 mb-2">{t('support.attachments')}</label>
                {newTicketAttachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {newTicketAttachments.map((src, idx) => (
                      <ImagePreview 
                        key={idx} 
                        src={src} 
                        onRemove={() => setNewTicketAttachments(prev => prev.filter((_, i) => i !== idx))}
                        small
                      />
                    ))}
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => handleImageUpload(e.target.files?.[0], true)}
                  accept="image/*"
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 border border-dashed border-zinc-700 rounded-lg hover:border-zinc-600 transition text-sm text-zinc-400 w-full justify-center"
                >
                  {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <ImageIcon className="w-4 h-4" />
                  )}
                  {t('support.uploadImage')}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleCreateTicket} disabled={creating} className="bg-amber-600 hover:bg-amber-700">
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                {t('support.createTicket')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Support;
